import { Observer, observer } from "mobx-react-lite";

import {
  dataAttribute,
  DataAttributes,
  DetailsList,
  FontIcon,
  FontSizes,
  IColumn,
  Link,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { ActivityDescriptionDto } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ClinicalActivityType } from "@modules/clinical/screens/patient-record/components/clinical-activity/types/clinical-activity.type.ts";
import { ActivityDescription } from "@stores/clinical/models/ActivityDescription.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";
import { DefaultNoResultsTile } from "@ui-components/InfiniteScrollList/DefaultNoResultsTile.tsx";

interface ClinicalReminderListProps {
  setShowClinicalReminderPreferenceDialog: (value: boolean) => void;
  setSelectedClinicalReminder: (
    activityDescription: ActivityDescription | undefined
  ) => void;
}

interface ClinicalReminderPreferenceFilterBarProps {
  searchText?: string;
  isActive: string[];
}

export const ClinicalReminderList = observer(
  ({
    setShowClinicalReminderPreferenceDialog,
    setSelectedClinicalReminder
  }: ClinicalReminderListProps) => {
    const { clinical, core } = useStores();

    const hasActivityDescriptionWritePermission = core.hasPermissions(
      Permission.ActivityDescriptionWrite
    );

    const columns: IColumn[] = [
      {
        name: "Reason",
        key: "reminder-reason",
        minWidth: 100,
        onRender: (activityDescription: ActivityDescription) => (
          <Observer>
            {() => (
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
              >
                {hasActivityDescriptionWritePermission ? (
                  <Link
                    onClick={() => {
                      setShowClinicalReminderPreferenceDialog(true);
                      setSelectedClinicalReminder(activityDescription);
                    }}
                  >
                    {activityDescription.reasonText}
                  </Link>
                ) : (
                  <Text>{`${activityDescription.reasonText} `}</Text>
                )}

                {activityDescription.clinicallySignificant && (
                  <TextBadge
                    badgeColor={TextBadgeColor.blue}
                    badgeSize={TextBadgeSize.small}
                  >
                    <FontIcon
                      iconName="UserOptional"
                      styles={{
                        root: {
                          fontSize: FontSizes.size16,

                          verticalAlign: "middle"
                        }
                      }}
                    />
                  </TextBadge>
                )}
              </Stack>
            )}
          </Observer>
        )
      },
      {
        name: "Default Interval",
        key: "defaultInterval",
        minWidth: 120,
        onRender: (activityDescription: ActivityDescription) => (
          <Observer>
            {() => {
              if (
                !activityDescription.interval &&
                !activityDescription.frequency
              ) {
                return null;
              }

              return (
                <Text>{`${activityDescription.interval ?? ""} ${
                  activityDescription.frequency ?? ""
                }`}</Text>
              );
            }}
          </Observer>
        )
      },
      {
        name: "Status",
        key: "status",
        minWidth: 120,
        onRender: (activityDescription: ActivityDescription) => (
          <Observer>
            {() => (
              <Text>
                {activityDescription.isInactive ? "Inactive" : "Active"}
              </Text>
            )}
          </Observer>
        )
      }
    ];

    // Filter for items
    const filterItems = (
      filterProps: ClinicalReminderPreferenceFilterBarProps
    ) => {
      let items = Array.from(clinical.activityDescriptionMapValues ?? []);

      items = items.filter(
        x =>
          x.reasonText !== "Other" &&
          x.activityType === ClinicalActivityType.Reminder
      );

      items.sort((a: ActivityDescriptionDto, b: ActivityDescriptionDto) => {
        if (!a.isInactive && b.isInactive) {
          return -1;
        } else if (a.isInactive && !b.isInactive) {
          return 1;
        } else {
          return 0;
        }
      });

      const searchText = filterProps.searchText;

      if (searchText) {
        items = items.filter(x =>
          x.reasonText
            .toLocaleLowerCase()
            .includes(searchText.toLocaleLowerCase())
        );
      }

      if (filterProps.isActive && filterProps.isActive.length > 0) {
        items = items.filter(x =>
          filterProps.isActive.includes(x.isInactive ? "Inactive" : "Active")
        );
      }

      return items;
    };

    return (
      <FilterBar<ClinicalReminderPreferenceFilterBarProps>
        initialValues={{ isActive: ["Active"], searchText: "" }}
        items={[
          {
            name: "searchText",
            stickItem: true,
            type: "searchBox",
            props: {
              ...dataAttribute(DataAttributes.Element, "search-box"),
              placeholder: "Search reason",
              styles: {
                root: {
                  width: 400
                }
              }
            }
          },
          {
            type: "optionsSelect",
            name: "isActive",
            props: {
              placeholder: "Status",
              label: "Status",
              options: [
                { key: "Active", text: "Active" },
                { key: "Inactive", text: "Inactive" }
              ],
              hideSearchOption: true,
              calloutWidth: 130,
              multiSelect: true
            }
          }
        ]}
      >
        {filtered => (
          <Observer>
            {() => {
              const filteredItems = filterItems(filtered.values);

              if (filteredItems.length === 0) {
                return <DefaultNoResultsTile defaultText="No matches found" />;
              }

              return <DetailsList items={filteredItems} columns={columns} />;
            }}
          </Observer>
        )}
      </FilterBar>
    );
  }
);
